import { FileDownload } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import { saveAs } from "file-saver";
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx-js-style";
import { theme } from "../Theme/theme";
import { GET } from "./BaseService";

const toExcelRgb = (hex: string) => hex.replace("#", "").toUpperCase();

const EXCEL_COLORS = {
    primary: toExcelRgb(theme.palette.primary.main),
    secondary: toExcelRgb(theme.palette.secondary.main),
    textOnPrimary: toExcelRgb(theme.palette.secondary.main),
    textOnSecondary: toExcelRgb(theme.palette.common.white),
};

type CellStyle = {
    font?: {
        bold?: boolean;
        sz?: number;
        color?: { rgb: string };
    };
    alignment?: {
        horizontal?: "center" | "left" | "right";
        vertical?: "center";
        wrapText?: boolean;
    };
    fill?: {
        patternType: "solid";
        fgColor: { rgb: string };
    };
};

const TITLE_HEADER_STYLE: CellStyle = {
    font: {
        bold: true,
        sz: 16,
        color: { rgb: EXCEL_COLORS.textOnPrimary },
    },
    alignment: { horizontal: "center", vertical: "center" },
    fill: { patternType: "solid", fgColor: { rgb: EXCEL_COLORS.primary } },
};

const LOCATION_HEADER_STYLE: CellStyle = {
    font: {
        bold: true,
        sz: 12,
        color: { rgb: EXCEL_COLORS.textOnSecondary },
    },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    fill: { patternType: "solid", fgColor: { rgb: EXCEL_COLORS.secondary } },
};

const TABLE_HEADER_STYLE: CellStyle = {
    font: {
        bold: true,
        sz: 11,
        color: { rgb: EXCEL_COLORS.textOnSecondary },
    },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    fill: { patternType: "solid", fgColor: { rgb: EXCEL_COLORS.secondary } },
};

const applyCellStyle = (
    ws: XLSX.WorkSheet,
    row: number,
    col: number,
    style: CellStyle
) => {
    const ref = XLSX.utils.encode_cell({ r: row, c: col });
    if (!ws[ref]) return;
    ws[ref].s = { ...(ws[ref].s || {}), ...style };
};

const styleExportSheet = (
    ws: XLSX.WorkSheet,
    colCount: number,
    titleRowCount: number,
    reportTitleLineCount: number,
    tableHeaderRowIndex: number
) => {
    if (colCount < 1) return;

    const merges: XLSX.Range[] = [];

    for (let r = 0; r < titleRowCount; r++) {
        merges.push({ s: { r, c: 0 }, e: { r, c: colCount - 1 } });
        const isReportTitleRow = r < reportTitleLineCount;
        applyCellStyle(
            ws,
            r,
            0,
            isReportTitleRow ? TITLE_HEADER_STYLE : LOCATION_HEADER_STYLE
        );
    }

    for (let c = 0; c < colCount; c++) {
        applyCellStyle(ws, tableHeaderRowIndex, c, TABLE_HEADER_STYLE);
    }

    ws["!merges"] = [...(ws["!merges"] || []), ...merges];

    ws["!rows"] = ws["!rows"] || [];
    for (let r = 0; r < titleRowCount; r++) {
        ws["!rows"][r] = { hpt: r === 0 ? 32 : 26 };
    }
    ws["!rows"][tableHeaderRowIndex] = { hpt: 22 };

    const colWidths = ws["!cols"] || [];
    for (let c = 0; c < colCount; c++) {
        colWidths[c] = { wch: Math.max(colWidths[c]?.wch || 12, 14) };
    }
    ws["!cols"] = colWidths;
};

type Props = {
    fileName: string;
    sheetName: string;
    targetRef: React.RefObject<HTMLTableElement>;
    reportDetails?: any;
};

/* ===================== CONFIG ===================== */

/** Columns that must always be TEXT in Excel */
const TEXT_COLUMNS = [
    "CNIC",
    "Meter",
    "CNIC No",
    "CNIC",
    "Meter No",
    "MeterNo",
    "meterNo",
    "Consumer No",
    "mobileNumber",
    "Phone",
    "Contact No",
    "meterNo",
];

/* ===================== HELPERS ===================== */

const safeName = (v = "") =>
    v.replace(/[\/\\:*?"<>|]/g, "_").replace(/\s+/g, "_");

const LOCATION_KEYS = [
    "district",
    "powerstation",
    "village",
    "hamlet",
] as const;

const LOCATION_LABELS: Record<(typeof LOCATION_KEYS)[number], string> = {
    district: "District",
    powerstation: "Powerstation",
    village: "Village",
    hamlet: "Hamlet",
};

const getFilterId = (details: any, key: string): string | null => {
    const raw = details?.[key];
    if (raw == null || raw === "" || raw === "all") return null;
    if (typeof raw === "object" && raw.value != null && raw.value !== "") {
        const v = raw.value;
        return v === "all" ? null : String(v);
    }
    return String(raw);
};

const hasLocationReportContext = (details: any) =>
    Boolean(
        details &&
            LOCATION_KEYS.some((key) => Object.prototype.hasOwnProperty.call(details, key))
    );

type LocationNames = {
    district: { name?: string } | null;
    powerstation: { name?: string } | null;
    village: { name?: string } | null;
    hamlet: { name?: string } | null;
};

/** Header line for Excel: selected names, or "All" when no location filter is applied */
const buildLocationHeaderLine = (
    reportDetails: any,
    names: LocationNames
): string[] => {
    if (!hasLocationReportContext(reportDetails)) return [];

    const selectedParts = LOCATION_KEYS.filter((key) =>
        Object.prototype.hasOwnProperty.call(reportDetails, key)
    )
        .map((key) => {
            const id = getFilterId(reportDetails, key);
            if (!id) return null;
            const label = LOCATION_LABELS[key];
            const displayName = names[key]?.name?.trim();
            return `${label}: ${displayName || "..."}`;
        })
        .filter(Boolean) as string[];

    if (selectedParts.length) return [selectedParts.join(" | ")];

    return ["All"];
};

const formatReportTitle = (name: string) =>
    name.replace(/__/g, " ").replace(/_/g, " ").trim();

const extractTableData = (table: HTMLTableElement | null) => {
    if (!table) return { headers: [], dataRows: [] };

    const allRows = Array.from(table.querySelectorAll("tr")).filter(
        (r) => r.querySelectorAll("td, th").length > 0
    );

    if (!allRows.length) return { headers: [], dataRows: [] };

    const headerRow = allRows.find((row) =>
        Array.from(row.querySelectorAll("th, td")).some((c) =>
            c.textContent?.trim()
        )
    )!;

    const headers = Array.from(headerRow.querySelectorAll("th, td")).map(
        (c) => c.textContent?.trim() || ""
    );

    const headerIndex = allRows.indexOf(headerRow);

    const dataRows = allRows
        .slice(headerIndex + 1)
        .map((row) =>
            Array.from(row.querySelectorAll("td, th")).map(
                (c) => c.textContent?.trim() || ""
            )
        );

    return { headers, dataRows };
};

/** Convert values correctly based on column header */
const normalizeExcelValue = (value: string, header: string) => {
    if (value === "" || value == null) return "";

    // force specific columns as TEXT
    if (TEXT_COLUMNS.includes(header)) {
        return String(value);
    }

    // try converting to number
    const clean = value.replace(/,/g, "").trim();
    if (clean !== "" && !isNaN(Number(clean))) {
        return Number(clean);
    }

    return value;
};

/* ===================== COMPONENT ===================== */

export const TableToExcel = ({
    fileName,
    sheetName,
    targetRef,
    reportDetails,
}: Props) => {
    const [district, setDistrict] = useState<any>(null);
    const [village, setVillage] = useState<any>(null);
    const [hamlet, setHamlet] = useState<any>(null);
    const [powerstation, setPowerstation] = useState<any>(null);

    const fetchDistrict = async (id: string) => {
        if (!id) return setDistrict(null);
        const res = await GET("/region/district", {
            page: 1,
            limit: 10000,
            search: "",
            id,
        });
        setDistrict(res?.data?.rows?.[0] || null);
    };

    const fetchVillage = async (id: string) => {
        if (!id) return setVillage(null);
        const res = await GET("/region/village", {
            page: 1,
            limit: 10000,
            search: "",
            id,
        });
        setVillage(res?.data?.rows?.[0] || null);
    };

    const fetchHamlet = async (id: string) => {
        if (!id) return setHamlet(null);
        const res = await GET("/region/hamlet", {
            page: 1,
            limit: 10000,
            search: "",
            id,
        });
        setHamlet(res?.data?.rows?.[0] || null);
    };

    const fetchPowerStation = async (id: string) => {
        if (!id) return setPowerstation(null);
        const res = await GET("/powerstation", {
            page: 1,
            limit: 10000,
            search: "",
            id,
        });
        setPowerstation(res?.data?.rows?.[0] || null);
    };

    useEffect(() => {
        if (!reportDetails) {
            setDistrict(null);
            setVillage(null);
            setHamlet(null);
            setPowerstation(null);
            return;
        }
        fetchDistrict(getFilterId(reportDetails, "district") || "");
        fetchVillage(getFilterId(reportDetails, "village") || "");
        fetchHamlet(getFilterId(reportDetails, "hamlet") || "");
        fetchPowerStation(getFilterId(reportDetails, "powerstation") || "");
    }, [reportDetails]);

    const handleExport = () => {
        const { headers, dataRows } = extractTableData(targetRef.current);
        if (!headers.length && !dataRows.length) return;

        /* ----- File Name ----- */
        const nameParts: string[] = [];
        if (district?.name)
            nameParts.push(`District_${safeName(district.name)}`);
        if (powerstation?.name)
            nameParts.push(`PS_${safeName(powerstation.name)}`);
        if (village?.name) nameParts.push(`Village_${safeName(village.name)}`);
        if (hamlet?.name) nameParts.push(`Hamlet_${safeName(hamlet.name)}`);

        const finalFileName = nameParts.length
            ? `${fileName}__${nameParts.join("__")}`
            : fileName;

        /* ----- Build Excel Rows ----- */

        const updatedHeaders = headers.filter(
            (h) => h !== "actions" && h !== "Edit"
        );

        const actionIndexes = headers
            .map((h, i) =>
                h === "actions" || h === "Edit" ? i : -1
            )
            .filter((i) => i >= 0);

        const filteredDataRows = dataRows.map((row) =>
            row.filter((_, i) => !actionIndexes.includes(i))
        );

        const titleRows: string[][] = [];
        const reportTitle = formatReportTitle(fileName);
        const reportTitleLineCount = reportTitle ? 1 : 0;
        if (reportTitle) titleRows.push([reportTitle]);

        const locationHeader = buildLocationHeaderLine(reportDetails, {
            district,
            powerstation,
            village,
            hamlet,
        });
        titleRows.push(...locationHeader.map((line) => [line]));

        const excelRows = [
            ...titleRows,
            ...(titleRows.length ? [[]] : []),
            updatedHeaders,
            ...filteredDataRows.map((row) =>
                row.map((cell, i) =>
                    normalizeExcelValue(cell, updatedHeaders[i])
                )
            ),
        ];

        /* ----- Export ----- */
        const colCount = updatedHeaders.length;
        const titleRowCount = titleRows.length;
        const tableHeaderRowIndex =
            titleRowCount + (titleRowCount > 0 ? 1 : 0);

        const ws = XLSX.utils.aoa_to_sheet(excelRows);
        if (titleRowCount > 0 || colCount > 0) {
            styleExportSheet(
                ws,
                colCount,
                titleRowCount,
                reportTitleLineCount,
                tableHeaderRowIndex
            );
        }

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName || "Sheet1");

        const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

        saveAs(
            new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }),
            `${finalFileName}.xlsx`
        );
    };

    return (
        <Tooltip title="Export To Excel (XLSX)">
            <IconButton size="small" onClick={handleExport}>
                <FileDownload fontSize="small" />
            </IconButton>
        </Tooltip>
    );
};
